import { useContext } from '@/compat';
import { isFunction } from '@/helpers/misc';
import useAlert from '../../misc/dialog/useAlert';

/**
 * This function checks whether the cropped images suitable the settings and warns the user if the settings are not suitable.
 * @param {function, object} minimumResolution Cropped image minimum resolution settings
 * @returns {array} assertImageSize - If image resolution suitable to settings return true otherwise return false
 * @example{
 *   const minimumResolutionSettings = {width:540, height:680};
 *
 *   const imageToAssert = {width:320, height:150};
 *
 *   const [assertImageSize] = useImageSizeValidationByContentSettings(minimumResolutionSettings);
 *
 *   assertImageSize(imageToAssert);
 *
 *   return{
 *     true: Image size matches the settings
 *     false: Image size does not match the settings
 *   }
 * }
 */
const useImageSizeValidationByContentSettings = (minimumResolution, maximumResolution) => {
  const {
    $lang, $i18n,
  } = useContext();
  const alert = useAlert();

  const showImageSizeIsSmallAlert = (width, height) => {
    alert.error(
      $i18n.t($lang.app.WARNING),
      $i18n.t($lang.image.CHECK_IMAGE_SIZE_ALERT).replace('{0}', `${width}x${height}`),
    );
  };

  const showImageSizeIsBigAlert = (width, height) => {
    alert.error(
      $i18n.t($lang.app.WARNING),
      $i18n.t($lang.image.CHECK_IMAGE_MAX_SIZE_ALERT).replace('{0}', `${width}x${height}`),
    );
  };

  const showFileIsNotImageAlert = () => {
    alert.error(
      $i18n.t($lang.app.WARNING),
      $i18n.t($lang.image.CHECK_IMAGE_FILE_TYPE_ALERT),
    );
  };

  const assertImageSize = image => {
    const computedMinimumResolution = isFunction(minimumResolution) ? minimumResolution() : minimumResolution.value;

    if (computedMinimumResolution.width > image.width || computedMinimumResolution.height > image.height) {
      showImageSizeIsSmallAlert(computedMinimumResolution.width, computedMinimumResolution.height);

      return false;
    }

    if (maximumResolution) {
      const computedMaximumResolution = isFunction(maximumResolution) ? maximumResolution() : maximumResolution.value;

      if (computedMaximumResolution.width < image.width || computedMaximumResolution.height < image.height) {
        showImageSizeIsBigAlert(computedMaximumResolution.width, computedMaximumResolution.height);

        return false;
      }
    }

    return true;
  };

  const assertFileIsNotImage = () => {
    showFileIsNotImageAlert();

    return false;
  };

  return [
    assertImageSize,
    assertFileIsNotImage,
  ];
};

export default useImageSizeValidationByContentSettings;
